/**
 * VoiceMode.jsx — Rammy's streaming, full-duplex voice conversation.
 *
 * Replaces the batch record→transcribe→LLM→speak pipeline with a persistent
 * WebSocket pipe:
 *
 *     Browser (this component) ⇄ /api/voice/stream
 *                                ⇄ Deepgram Nova-3 (streaming STT)
 *                                ⇄ /api/chat/stream (LLM, internal loopback)
 *                                ⇄ ElevenLabs stream-input (streaming TTS)
 *
 * Why: end-of-turn → first audible byte drops from ~2.5-4 s (batch) to
 * ~800 ms-1.4 s (streaming). No blocking "Transcribing…" screen. Barge-in
 * is handled server-side via Deepgram's SpeechStarted events.
 *
 * External API (props):
 *   voice              — UI-facing voice name ("hale", "amber", …) or clone ID.
 *   conversationId     — existing conversation to append to; null = new conv.
 *   focusMode          — null | "walking" | "focus".
 *   onFocusModeChange  — (mode) => void; called when the user changes the chip.
 *   onClose            — () => void; called when user closes the overlay.
 *   onExchange         — ({user, assistant, conversationId, latency}) => void;
 *                        called at the end of every completed voice turn so
 *                        the parent can append to its chat transcript.
 *   onConversationCreated — (id) => void; called the first time the server
 *                        emits a fresh conversation_id.
 */
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Mic, X, Loader2, Volume2, Sparkles } from "lucide-react";
import Ring from "@/components/Ring";
import SourceBadge from "@/components/SourceBadge";
import { useVoiceStream } from "@/hooks/useVoiceStream";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const ERROR_COPY = {
  mic_denied: "Your browser is blocking the microphone. Allow mic access for this site, then hit Retry.",
  mic_missing: "I can't find a microphone. Check your input device, then hit Retry.",
  mic_pending: "Still waiting on the microphone prompt — allow access at the top of your browser, then hit Retry.",
  mic_failed: "Something else is holding the microphone. Close whatever is using it, then hit Retry.",
  audio_engine_failed: "This browser blocked the audio engine. Reload the page, or try Chrome or Safari.",
  auth_expired: "Your session expired. Sign in again and voice comes straight back.",
  ws_failed: "I couldn't keep the line open — that's usually the network. Hit Retry.",
  stt_unavailable: "My ears didn't come up that time. Give it a moment and hit Retry.",
  default: "Voice hit a snag. Hit Retry and I'll pick it back up.",
};

export default function VoiceMode({
  voice = "hale",
  conversationId = null,
  focusMode = null,
  anonSessionId = null,   // marketing page visitor — no account needed
  onFocusModeChange,
  onClose,
  onExchange,
  onConversationCreated,
  onOpenMyVoice,   // () => void — parent handles route to /app/myvoice
}) {
  // My-Voice invitation UI state. On `myvoice_invite` from the server the
  // overlay shows a small intro card explaining the formal enrollment flow.
  // The primary CTA closes voice mode and navigates to /app → myvoice screen
  // so the user can go through the full consented enrollment.
  const [inviteOpen, setInviteOpen] = useState(false);

  // Keep the freshest conversation_id in a ref so onTurnDone (a closure
  // captured at hook init time) ships the *current* value when it fires, not
  // a stale null from the first render.
  const conversationIdRef = useRef(conversationId);
  useEffect(() => { conversationIdRef.current = conversationId; }, [conversationId]);

  const {
    state, level, partial, finalText, assistantText, latency,
    credibility, sources, progress, narration, errorCode, creditsPerMinute,
    start, stop, cancel, setFocusMode: pushFocusMode,
    dismissMyVoiceInvite,
  } = useVoiceStream({
    voice,
    conversationId,
    focusMode,
    anonSessionId,
    onTurnDone: (assistant, lat) => {
      if (onExchange) {
        onExchange({
          user: finalTextRef.current,
          assistant,
          conversationId: conversationIdRef.current,
          latency: lat,
        });
      }
    },
    onNewConversation: (cid) => {
      conversationIdRef.current = cid;
      if (onConversationCreated) onConversationCreated(cid);
    },
    onMyVoiceInvite: () => setInviteOpen(true),
  });

  // Keep the freshest finalText in a ref so onTurnDone reads the current
  // value at turn completion, not stale.
  const finalTextRef = useRef("");
  useEffect(() => { finalTextRef.current = finalText || ""; }, [finalText]);

  // Kick off the pipeline on mount. React 18 StrictMode runs this
  // mount → cleanup → mount in dev; a "have I started?" ref guard used to
  // swallow the *second* mount, which is the one that survives — so voice
  // never started at all and the ring span on "waking up" for ever. start()
  // is generation-guarded internally instead (see useVoiceStream), so the
  // honest start/stop pair is correct and safe.
  useEffect(() => {
    start();
    return () => stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Push focus-mode changes down to the server (keeps the reply shape in sync).
  useEffect(() => {
    if (state === "listening" || state === "thinking" || state === "speaking") {
      pushFocusMode(focusMode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusMode]);

  // Say what actually went wrong. "Voice pipeline failed" helped nobody: a
  // blocked microphone, a missing microphone, an expired session and a dead
  // socket all need different actions from the person reading it.
  useEffect(() => {
    if (state !== "error") return;
    if (errorCode === "insufficient_credits" || errorCode === "anon_limit") return;
    toast.error(ERROR_COPY[errorCode] || ERROR_COPY.default, { duration: 8000 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, errorCode]);

  // Compute visual state (halo colour + scale) once so JSX stays clean.
  const listening = state === "listening";
  const speaking = state === "speaking";
  const thinking = state === "thinking";
  // One state machine for the Ring, shared with the text route.
  const ringState =
    state === "error" ? "error"
    : listening ? "listening"
    : thinking ? "thinking"
    : speaking ? "speaking"
    : "idle";

  return (
    <div
      className="fixed inset-0 z-[100] bg-[var(--rm-ink-900)] text-white flex flex-col items-center justify-center px-6"
      data-testid="voice-mode-overlay"
    >
      <button
        onClick={onClose}
        className="absolute top-6 right-6 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition"
        aria-label="Close voice mode"
        data-testid="voice-mode-close"
      >
        <X size={18} />
      </button>

      {/* Focus-mode segmented control (top-left) */}
      <div className="absolute top-6 left-6 inline-flex items-center gap-1 bg-white/10 rounded-full p-1" data-testid="voice-focus-mode">
        {[
          { id: null,       label: "Normal",  desc: "Two-sentence replies" },
          { id: "walking",  label: "Walking", desc: "One-line replies, no screen actions" },
          { id: "focus",    label: "Focus",   desc: "Minimum interruption, one-line answers" },
        ].map((m) => {
          const active = (focusMode || null) === m.id;
          return (
            <button
              key={m.id ?? "normal"}
              onClick={() => onFocusModeChange?.(m.id)}
              title={m.desc}
              data-testid={`voice-focus-mode-${m.id ?? "normal"}`}
              className={`px-3 h-8 rounded-full text-[11.5px] font-medium transition ${
                active ? "bg-[var(--rm-surface)] text-[var(--rammy-text)]" : "text-white/70 hover:text-white"
              }`}
            >
              {m.label}
            </button>
          );
        })}
      </div>

      {/* THE RING — same component, same state machine as the text route.
          Tapping while Rammy is speaking cuts TTS and returns to listening. */}
      <div className="relative flex items-center justify-center" style={{ width: 300, height: 300 }}>
        <Ring
          state={ringState}
          size={200}
          amplitude={level}
          showLabel={false}
          onClick={speaking ? cancel : undefined}
          ariaLabel={speaking ? "Stop speaking and listen" : "Rammy"}
          testId="voice-ring"
        />
      </div>

      <div className="mt-8 min-h-[54px] text-center max-w-lg" data-testid="voice-mode-status">
        <div className="text-xs uppercase tracking-widest text-white/50">
          {state === "listening" && "Listening"}
          {state === "thinking"  && "Thinking"}
          {state === "speaking"  && "Speaking"}
          {state === "connecting" && "Waking up"}
          {state === "error" && (
            errorCode === "insufficient_credits" ? "Out of credits — voice is paused"
              : errorCode === "anon_limit" ? "Free trial done — make an account to keep talking"
              : errorCode === "mic_denied" ? "Microphone blocked — allow access, then tap to retry"
              : errorCode === "mic_missing" ? "No microphone found — tap to retry"
              : errorCode === "mic_pending" ? "Waiting on the microphone prompt — allow access, then tap"
              : errorCode === "auth_expired" ? "Session expired — sign in again"
              : errorCode === "stt_unavailable" ? "My ears didn't come up — tap to retry"
              : "Something went wrong — tap to retry")}
          {state === "idle"      && "Waking up"}
        </div>
        {creditsPerMinute && state !== "error" && (
          <div className="mt-1 font-mono text-[10px] text-white/30" data-testid="voice-mode-rate">
            {creditsPerMinute} credits a minute
          </div>
        )}

        {/* Live partial (while user is speaking) */}
        {partial && listening && (
          <div
            className="mt-2 text-sm text-white/80 leading-relaxed"
            data-testid="voice-mode-partial"
          >
            <span className="text-white/40">You: </span>“{partial}”
          </div>
        )}

        {/* Assistant streaming caption (while Rammy is replying) */}
        {(assistantText && (thinking || speaking)) && (
          <div
            className="mt-2 text-sm text-white/85 leading-relaxed"
            data-testid="voice-mode-assistant"
          >
            {assistantText}
          </div>
        )}

        {/* Research narration — mirrors what text chat shows above the reply
            bubble while `run_tools` is fetching from live sources. Kept subtle:
            small caps, no spinner (the ring already conveys "working"). */}
        {progress?.label && thinking && (
          <div
            className="mt-2 text-[11px] uppercase tracking-wider text-white/45"
            data-testid="voice-mode-progress"
          >
            {progress.label}
          </div>
        )}

        {/* What Rammy just said out loud ("Checking the weather.") — shown as
            well as spoken, for muted tabs and anyone who reads faster. */}
        {narration && thinking && !progress?.label && (
          <div
            className="mt-2 text-[11px] uppercase tracking-wider text-white/45"
            data-testid="voice-mode-narration"
          >
            {narration}
          </div>
        )}

        {/* Provenance pill — same component as text chat. Sources only, no
            confidence wording. Rendered on a dark background, so we sit the
            expanded plate on a translucent surface for legibility. */}
        {sources?.length ? (
          <div
            className="mt-3 flex justify-center"
            data-testid="voice-mode-sources"
          >
            <div className="[&_[data-testid=source-badge-details]]:!bg-[var(--rm-surface)]/95">
              <SourceBadge sources={sources} credibility={credibility}
                           query={finalText} apiBase={BACKEND} />
            </div>
          </div>
        ) : null}

        {/* Mic-level meter — reactive proof of hearing */}
        {(listening || thinking) && (
          <div className="mt-3 mx-auto h-1 w-40 rounded-full bg-white/10 overflow-hidden" data-testid="voice-mode-level">
            <div className="h-full bg-[var(--rammy-cyan)] transition-[width] duration-75"
                 style={{ width: `${Math.round(level * 100)}%` }} />
          </div>
        )}
      </div>

      <div className="mt-10 flex items-center gap-3">
        {state === "error" ? (
          <button
            onClick={() => { start(); }}
            className="btn-pill btn-primary h-11 px-5 inline-flex items-center gap-2"
            data-testid="voice-mode-retry"
          >
            <Mic size={16} /> Retry
          </button>
        ) : (
          <div className="flex items-center gap-2 text-xs text-white/50">
            {listening && <><Mic size={14} /> speak anytime — I&apos;ll listen while you talk</>}
            {thinking  && <><Loader2 size={14} className="animate-spin" /> composing…</>}
            {speaking  && <><Volume2 size={14} /> interrupt any time — I&apos;ll stop and listen</>}
            {state === "connecting" && <><Loader2 size={14} className="animate-spin" /> connecting…</>}
          </div>
        )}
      </div>

      {/* My-Voice invite — a small intro card that opens when the server
          detects an engaged session. The primary CTA closes voice mode and
          navigates to the dedicated /app/myvoice enrollment route; we NEVER
          clone from live conversation audio. */}
      {inviteOpen && (
        <div
          className="mt-6 w-full max-w-sm rounded-2xl px-4 py-4 backdrop-blur-md"
          style={{
            background: "linear-gradient(135deg, rgba(72,224,200,0.16), rgba(178,140,255,0.16))",
            border: "1px solid rgba(255,255,255,0.14)",
          }}
          data-testid="myvoice-invite"
        >
          <div className="flex items-start gap-3">
            <Sparkles size={18} className="text-[var(--rammy-cyan)] shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="text-[13.5px] text-white font-medium leading-snug">
                Want Rammy to speak in your own voice?
              </div>
              <div className="text-[12px] text-white/70 leading-snug mt-1">
                Enroll in ~90 seconds — you&apos;ll record a short guided script, review the result,
                and activate only if you&apos;re happy. You can pause or delete it anytime.
              </div>
              <div className="mt-3 flex items-center gap-2">
                <button
                  onClick={() => {
                    setInviteOpen(false);
                    if (onOpenMyVoice) onOpenMyVoice();
                  }}
                  className="btn-pill h-8 px-3 text-[12px] bg-[var(--rm-surface)] text-[var(--rammy-text)] font-medium hover:bg-[var(--rm-surface)]/90 inline-flex items-center gap-1"
                  data-testid="myvoice-invite-start"
                >
                  Set up My Voice
                </button>
                <button
                  onClick={() => { setInviteOpen(false); dismissMyVoiceInvite(); }}
                  className="text-[12px] text-white/60 hover:text-white/90 px-1"
                  data-testid="myvoice-invite-dismiss"
                >
                  Not now
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Latency instrumentation — only render in dev-ish contexts. Small,
          bottom-left, so the ops team can watch it during a real call. */}
      {latency && (
        <div className="fixed bottom-3 left-3 text-[10px] font-mono text-white/40" data-testid="voice-mode-latency">
          tts_first_byte: {latency.tts_first_byte ?? "-"}ms · llm_first_token: {latency.llm_first_token ?? "-"}ms · done: {latency.turn_done ?? "-"}ms
        </div>
      )}

      <button
        onClick={onClose}
        className="mt-8 text-xs text-white/40 hover:text-white/70 transition"
        data-testid="voice-mode-end"
      >
        End conversation
      </button>
    </div>
  );
}
